"use client"

import { useState, useMemo } from "react"
import { RotateCcw, Info } from "lucide-react"
import type { Idioma } from "@/lib/landing/idioma"
import { eurosEn } from "./formato"

const T = {
  es: {
    bajo: "Bajo",
    normal: "Normal",
    bueno: "Bueno",
    excelente: "Excelente",
    datosSala: "Datos de la sala",
    limpiar: "Limpiar",
    mesas: "Nº de mesas",
    plazasPorMesa: "Plazas por mesa",
    horasServicio: "Horas de servicio por día",
    duracionComida: "Duración media de comida (minutos)",
    tasaOcupacion: "Tasa de ocupación media (%)",
    ticketMedio: "Ticket medio por persona (€)",
    resultados: "Resultados",
    rotaciones: "Rotaciones por servicio",
    plazasTotales: (n: number) => `${n} plazas totales`,
    cubiertosTeor: "Cubiertos teóricos",
    alCien: "al 100% ocupación",
    cubiertosReales: "Cubiertos reales",
    alOcupacion: (n: number) => `al ${n}% ocupación`,
    ingresos: "Ingresos estimados",
    porServicio: "por servicio",
    queEs: "¿Qué es el RevPASH?",
    explicacion:
      "Revenue Per Available Seat Hour — ingresos por plaza disponible y hora. Es la métrica clave para medir la eficiencia de tu sala. Un RevPASH superior a 8€ se considera bueno en restauración casual.",
    referencia: "Referencia RevPASH",
    tramoBajo: "Bajo (<5€)",
    tramoNormal: "Normal (5-8€)",
    tramoBueno: "Bueno (8-12€)",
    tramoExcelente: "Excelente (>12€)",
  },
  en: {
    bajo: "Low",
    normal: "Average",
    bueno: "Good",
    excelente: "Excellent",
    datosSala: "Your dining room",
    limpiar: "Reset",
    mesas: "Number of tables",
    plazasPorMesa: "Seats per table",
    horasServicio: "Service hours per day",
    duracionComida: "Average meal length (minutes)",
    tasaOcupacion: "Average occupancy rate (%)",
    ticketMedio: "Average spend per guest (€)",
    resultados: "Results",
    rotaciones: "Table turns per service",
    plazasTotales: (n: number) => `${n} seats in total`,
    cubiertosTeor: "Theoretical covers",
    alCien: "at 100% occupancy",
    cubiertosReales: "Actual covers",
    alOcupacion: (n: number) => `at ${n}% occupancy`,
    ingresos: "Estimated revenue",
    porServicio: "per service",
    queEs: "What is RevPASH?",
    explicacion:
      "Revenue Per Available Seat Hour: what each available seat earns per hour. It is the key measure of how efficiently your dining room works. A RevPASH above €8 is considered good for casual dining.",
    referencia: "RevPASH benchmarks",
    tramoBajo: "Low (<€5)",
    tramoNormal: "Average (€5–8)",
    tramoBueno: "Good (€8–12)",
    tramoExcelente: "Excellent (>€12)",
  },
}

const defaultValues = {
  mesas: 15,
  plazasPorMesa: 4,
  horasServicio: 4,
  duracionComida: 75,
  tasaOcupacion: 75,
  ticketMedio: 22,
}

export default function RotacionMesasCalculator({ idioma = "es" }: { idioma?: Idioma }) {
  const t = T[idioma]
  const [mesas, setMesas] = useState(defaultValues.mesas)
  const [plazasPorMesa, setPlazasPorMesa] = useState(defaultValues.plazasPorMesa)
  const [horasServicio, setHorasServicio] = useState(defaultValues.horasServicio)
  const [duracionComida, setDuracionComida] = useState(defaultValues.duracionComida)
  const [tasaOcupacion, setTasaOcupacion] = useState(defaultValues.tasaOcupacion)
  const [ticketMedio, setTicketMedio] = useState(defaultValues.ticketMedio)

  const results = useMemo(() => {
    const totalPlazas = mesas * plazasPorMesa
    const rotaciones = duracionComida > 0 ? (horasServicio * 60) / duracionComida : 0
    const cubiertosTeor = totalPlazas * rotaciones
    const cubiertosReales = cubiertosTeor * (tasaOcupacion / 100)
    const ingresosEstimados = cubiertosReales * ticketMedio
    const revpash =
      totalPlazas > 0 && horasServicio > 0
        ? ingresosEstimados / (totalPlazas * horasServicio)
        : 0

    return {
      totalPlazas,
      rotaciones,
      cubiertosTeor: Math.floor(cubiertosTeor),
      cubiertosReales: Math.floor(cubiertosReales),
      ingresosEstimados,
      revpash,
    }
  }, [mesas, plazasPorMesa, horasServicio, duracionComida, tasaOcupacion, ticketMedio])

  const reset = () => {
    setMesas(defaultValues.mesas)
    setPlazasPorMesa(defaultValues.plazasPorMesa)
    setHorasServicio(defaultValues.horasServicio)
    setDuracionComida(defaultValues.duracionComida)
    setTasaOcupacion(defaultValues.tasaOcupacion)
    setTicketMedio(defaultValues.ticketMedio)
  }

  const getRevpashBenchmark = (val: number) => {
    if (val <= 0) return { label: "—", color: "text-zinc-400", bg: "bg-zinc-100" }
    if (val < 5) return { label: t.bajo, color: "text-red-600", bg: "bg-red-50" }
    if (val < 8) return { label: t.normal, color: "text-amber-600", bg: "bg-amber-50" }
    if (val < 12) return { label: t.bueno, color: "text-lime-600", bg: "bg-lime-50" }
    return { label: t.excelente, color: "text-emerald-600", bg: "bg-emerald-50" }
  }

  const revpashBm = getRevpashBenchmark(results.revpash)

  const inputClass =
    "w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent transition-shadow"

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
      <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-zinc-200">
        {/* Inputs */}
        <div className="p-6 md:p-8 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-zinc-900">{t.datosSala}</h2>
            <button
              onClick={reset}
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              {t.limpiar}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                {t.mesas}
              </label>
              <input
                type="number"
                value={mesas || ""}
                onChange={(e) => setMesas(parseInt(e.target.value) || 0)}
                placeholder="15"
                min="0"
                step="1"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                {t.plazasPorMesa}
              </label>
              <input
                type="number"
                value={plazasPorMesa || ""}
                onChange={(e) => setPlazasPorMesa(parseInt(e.target.value) || 0)}
                placeholder="4"
                min="0"
                step="1"
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.horasServicio}
            </label>
            <input
              type="number"
              value={horasServicio || ""}
              onChange={(e) => setHorasServicio(parseFloat(e.target.value) || 0)}
              placeholder="4"
              min="0"
              step="0.5"
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.duracionComida}
            </label>
            <input
              type="number"
              value={duracionComida || ""}
              onChange={(e) => setDuracionComida(parseInt(e.target.value) || 0)}
              placeholder="75"
              min="1"
              step="5"
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.tasaOcupacion}
            </label>
            <input
              type="number"
              value={tasaOcupacion || ""}
              onChange={(e) =>
                setTasaOcupacion(parseFloat(e.target.value) || 0)
              }
              placeholder="75"
              min="0"
              max="100"
              step="5"
              className={inputClass}
            />
            <div className="mt-2 h-2 rounded-full bg-zinc-200 overflow-hidden">
              <div
                className="h-full bg-rose-500 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(tasaOcupacion, 100)}%` }}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.ticketMedio}
            </label>
            <input
              type="number"
              value={ticketMedio || ""}
              onChange={(e) => setTicketMedio(parseFloat(e.target.value) || 0)}
              placeholder="22"
              min="0"
              step="any"
              className={inputClass}
            />
          </div>
        </div>

        {/* Results */}
        <div className="p-6 md:p-8 bg-zinc-50 space-y-6">
          <h2 className="text-lg font-semibold text-zinc-900">{t.resultados}</h2>

          {/* Rotaciones */}
          <div className="rounded-2xl p-6 text-center bg-rose-50">
            <p className="text-sm font-medium text-zinc-600 mb-1">
              {t.rotaciones}
            </p>
            <p className="text-5xl font-bold text-rose-600">
              {results.rotaciones > 0 ? results.rotaciones.toFixed(1) : "0.0"}
            </p>
            <p className="text-sm text-zinc-500 mt-2">
              {t.plazasTotales(results.totalPlazas)}
            </p>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <p className="text-xs text-zinc-500 mb-1">{t.cubiertosTeor}</p>
              <p className="text-xl font-bold text-zinc-900">
                {results.cubiertosTeor}
              </p>
              <p className="text-xs text-zinc-400">{t.alCien}</p>
            </div>
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <p className="text-xs text-zinc-500 mb-1">{t.cubiertosReales}</p>
              <p className="text-xl font-bold text-rose-600">
                {results.cubiertosReales}
              </p>
              <p className="text-xs text-zinc-400">{t.alOcupacion(tasaOcupacion)}</p>
            </div>
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <p className="text-xs text-zinc-500 mb-1">{t.ingresos}</p>
              <p className="text-xl font-bold text-emerald-600">
                {idioma === "en"
                  ? eurosEn(results.ingresosEstimados, 0)
                  : `${results.ingresosEstimados.toLocaleString("es-ES", { maximumFractionDigits: 0 })}€`}
              </p>
              <p className="text-xs text-zinc-400">{t.porServicio}</p>
            </div>
            <div className={`rounded-xl border border-zinc-200 p-4 ${revpashBm.bg}`}>
              <p className="text-xs text-zinc-500 mb-1">RevPASH</p>
              <p className={`text-xl font-bold ${revpashBm.color}`}>
                {idioma === "en" ? eurosEn(results.revpash) : `${results.revpash.toFixed(2)}€`}
              </p>
              <p className="text-xs text-zinc-400">{revpashBm.label}</p>
            </div>
          </div>

          {/* RevPASH explanation */}
          <div className="flex items-start gap-3 p-4 rounded-xl bg-white border border-zinc-200">
            <Info className="w-5 h-5 text-zinc-400 shrink-0 mt-0.5" />
            <div className="text-sm text-zinc-600">
              <p className="font-medium text-zinc-900 mb-1">{t.queEs}</p>
              <p>{t.explicacion}</p>
            </div>
          </div>

          {/* Benchmarks */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-zinc-700">{t.referencia}</p>
            <div className="flex rounded-full h-4 overflow-hidden">
              <div className="w-[25%] bg-red-200" />
              <div className="w-[25%] bg-amber-200" />
              <div className="w-[25%] bg-lime-200" />
              <div className="w-[25%] bg-emerald-200" />
            </div>
            <div className="flex justify-between text-xs text-zinc-500">
              <span>{t.tramoBajo}</span>
              <span>{t.tramoNormal}</span>
              <span>{t.tramoBueno}</span>
              <span>{t.tramoExcelente}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
